import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const WalletUI = () => {
  const [balance, setBalance] = useState('0.00');
  const [usd, setUsd] = useState(1);
  const [name, setName] = useState('');
  const [uid, setUid] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [activePortfolioTab, setActivePortfolioTab] = useState('All');
  const [activeMarketTab, setActiveMarketTab] = useState('mcx');
  const [showBalance, setShowBalance] = useState(true);
  const navigate = useNavigate();
  
  const [mcxData, setMcxData] = useState([]);
  const [currencyPairs, setCurrencyPairs] = useState([]);
  const [metalsData, setMetalsData] = useState([]);
  const [cryptoData, setCryptoData] = useState([]);
  const [loading, setLoading] = useState(true);

  const marketTabs = [
    { key: 'mcx', title: 'MCX', color: '#D4AF37', icon: 'bi-graph-up' },
    { key: 'forex', title: 'FOREX', color: '#4ECDC4', icon: 'bi-globe' },
    { key: 'crypto', title: 'CRYPTO', color: '#FFD93D', icon: 'bi-currency-bitcoin' },
    { key: 'metal', title: 'METAL', color: '#6B5B95', icon: 'bi-gem' },
  ];

  const fetchMcxData = async () => {
    try {
      const response = await fetch('https://singlewindowsecurities.com/rest3/metals_api');
      const data = await response.json();
      setMcxData(data);
    } catch (error) {
      console.error('MCX API Error:', error);
    }
  };

  const fetchForexData = async () => {
    try {
      const url = 'https://aurofxmarket.in/Rest/home_forex_api/';
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setCurrencyPairs(data.forex || []);
      setMetalsData(data.metals || []);
      setCryptoData(data.crypto || []);
    } catch (err) {
      console.error("Forex API Error:", err);
    }
  };

  const fetchUserData = async () => {
    try {
      const uid = localStorage.getItem("userid");
      const name = localStorage.getItem("name");
      setName(name || 'User');
      setUid(uid);
      if (uid) {
        const response = await fetch(`https://singlewindowsecurities.com/rest3/wallet_api/${uid}`);
        const responseJson = await response.json();
        setBalance(responseJson.wallet || '0.00');
        setUsd(responseJson.exchg_rate || 1);
      }
    } catch (error) {
      console.error('User Data Error:', error);
    }
  };

  const refreshAllData = async () => {
    setRefreshing(true);
    await Promise.all([fetchMcxData(), fetchForexData(), fetchUserData()]);
    setRefreshing(false);
    setLoading(false);
  };

  useEffect(() => {
    refreshAllData();
    const mcxInterval = setInterval(fetchMcxData, 2000);
    const forexInterval = setInterval(fetchForexData, 2000);
    return () => {
      clearInterval(mcxInterval);
      clearInterval(forexInterval);
    };
  }, []);

  const navigateToBuy = (item, type) => {
    if (type === 'mcx') {
      navigate("/buy/" + item.metal);
    } else {
      navigate("/buy2/" + item.curr_pipid + '/' + item.curr_code + '/' + item.chart_id);
    }
  };

  const renderMarketData = () => {
    let data = [];
    let emptyMessage = '';
    
    switch (activeMarketTab) {
      case 'mcx': data = mcxData; emptyMessage = 'Loading MCX...'; break;
      case 'forex': data = currencyPairs; emptyMessage = 'Loading Forex...'; break;
      case 'crypto': data = cryptoData; emptyMessage = 'Loading Crypto...'; break;
      case 'metal': data = metalsData; emptyMessage = 'Loading Metals...'; break;
    }

    if (data.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#D4AF37' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3" style={{ color: '#888' }}>{emptyMessage}</p>
        </div>
      );
    }

    return data.map((item, index) => {
      const tabInfo = marketTabs.find(tab => tab.key === activeMarketTab);
      const isPositive = parseFloat(item.chg || item.curr_chg || 0) >= 0;
      
      if (activeMarketTab === 'mcx') {
        return (
          <div 
            key={`mcx-${item.metal || item.id || index}`}
            className="market-card"
            onClick={() => navigateToBuy(item, 'mcx')}
            style={{
              background: 'rgba(26, 26, 26, 0.8)',
              borderRadius: '16px',
              marginBottom: '12px',
              padding: '16px',
              cursor: 'pointer',
              border: '1px solid rgba(212, 175, 55, 0.15)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#D4AF37';
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.15)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div className="d-flex align-items-center">
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(212, 175, 55, 0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px'
                }}>
                  <i className="fa fa-gem" style={{ color: '#D4AF37', fontSize: '20px' }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: '#fff' }}>{item.metal || 'MCX'}</h6>
                  <small style={{ color: '#888' }}>{item.cont_date || 'Spot'}</small>
                </div>
              </div>
              <div className="text-end">
                <h6 className="mb-0 fw-bold" style={{ color: '#D4AF37' }}>₹{item.rate || '0.00'}</h6>
                <span style={{ 
                  color: isPositive ? '#00C853' : '#FF5252',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  <i className={`fa fa-arrow-${isPositive ? 'up' : 'down'} me-1`}></i>
                  {Math.abs(parseFloat(item.chg || 0)).toFixed(2)}%
                </span>
              </div>
            </div>
            <div className="row g-2 pt-2" style={{ borderTop: '1px solid rgba(212, 175, 55, 0.1)' }}>
              <div className="col-4 text-center">
                <small style={{ color: '#666', fontSize: '10px' }}>OPEN</small>
                <div style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>₹{item.open || '0.00'}</div>
              </div>
              <div className="col-4 text-center">
                <small style={{ color: '#666', fontSize: '10px' }}>HIGH</small>
                <div style={{ color: '#FF5252', fontSize: '12px', fontWeight: '500' }}>₹{item.high || '0.00'}</div>
              </div>
              <div className="col-4 text-center">
                <small style={{ color: '#666', fontSize: '10px' }}>LOW</small>
                <div style={{ color: '#00C853', fontSize: '12px', fontWeight: '500' }}>₹{item.low || '0.00'}</div>
              </div>
            </div>
          </div>
        );
      } else {
        const sellPrice = parseFloat(item.csell_price || 0);
        const buyPrice = parseFloat(item.cbuy_price || 0);
        const spread = ((buyPrice - sellPrice) / sellPrice * 100).toFixed(2);
        
        return (
          <div 
            key={`${activeMarketTab}-${index}`}
            className="market-card"
            onClick={() => navigateToBuy(item, 'forex')}
            style={{
              background: 'rgba(26, 26, 26, 0.8)',
              borderRadius: '16px',
              marginBottom: '12px',
              padding: '16px',
              cursor: 'pointer',
              border: '1px solid rgba(212, 175, 55, 0.15)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#D4AF37';
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.15)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div className="d-flex align-items-center">
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: `${tabInfo?.color}15`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px'
                }}>
                  <i className={`bi ${tabInfo?.icon}`} style={{ color: tabInfo?.color, fontSize: '20px' }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: '#fff' }}>{item.curr_name || `${tabInfo?.title}`}</h6>
                  <small style={{ color: '#888' }}>{item.curr_code || ''}</small>
                </div>
              </div>
              <div>
                <span style={{ 
                  background: isPositive ? 'rgba(0, 200, 83, 0.15)' : 'rgba(255, 82, 82, 0.15)',
                  color: isPositive ? '#00C853' : '#FF5252',
                  padding: '4px 8px',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  <i className={`fa fa-arrow-${isPositive ? 'up' : 'down'} me-1`}></i>
                  {Math.abs(parseFloat(item.curr_chg || 0.15)).toFixed(2)}%
                </span>
              </div>
            </div>
            <div className="row g-2">
              <div className="col-6 text-center">
                <small style={{ color: '#666', fontSize: '10px' }}>SELL</small>
                <div style={{ color: '#FF5252', fontSize: '14px', fontWeight: 'bold' }}>
                  {activeMarketTab === 'crypto' || activeMarketTab === 'metal' ? '$' : '₹'}{sellPrice.toFixed(4)}
                </div>
              </div>
              <div className="col-6 text-center">
                <small style={{ color: '#666', fontSize: '10px' }}>BUY</small>
                <div style={{ color: '#00C853', fontSize: '14px', fontWeight: 'bold' }}>
                  {activeMarketTab === 'crypto' || activeMarketTab === 'metal' ? '$' : '₹'}{buyPrice.toFixed(4)}
                </div>
              </div>
            </div>
            <div className="mt-2 text-center">
              <small style={{ color: '#D4AF37', fontSize: '10px' }}>
                Spread: {spread}%
              </small>
            </div>
          </div>
        );
      }
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#0a0a0a' }}>
        <div className="text-center">
          <div className="spinner-border" style={{ color: '#D4AF37' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3" style={{ color: '#D4AF37' }}>Loading Market Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0" style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0d0d0d 100%)'
    }}>
      {/* Welcome Header */}
      <div className="container mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 style={{ color: '#fff', margin: 0 }}>Welcome back,</h5>
            <h4 style={{ color: '#D4AF37', fontWeight: 'bold', margin: 0 }}>{name}</h4>
          </div>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-sm"
              onClick={() => setShowBalance(!showBalance)}
              style={{
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '12px',
                width: '40px',
                height: '40px',
                color: '#D4AF37'
              }}
            >
              <i className={`fa fa-eye${showBalance ? '' : '-slash'}`}></i>
            </button>
            <button 
              className="btn btn-sm"
              onClick={refreshAllData}
              disabled={refreshing}
              style={{
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '12px',
                width: '40px',
                height: '40px',
                color: '#D4AF37'
              }}
            >
              <i className={`fa fa-arrow-repeat ${refreshing ? 'spin' : ''}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Balance Card - Redesigned */}
      <div className="container mt-2">
        <div style={{
          background: 'linear-gradient(145deg, rgba(212, 175, 55, 0.15), rgba(184, 150, 12, 0.05))',
          borderRadius: '24px',
          padding: '20px',
          border: '1px solid rgba(212, 175, 55, 0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <div className="text-center">
            <small style={{ color: '#D4AF37', letterSpacing: '2px', fontSize: '11px' }}>TOTAL BALANCE</small>
            <div className="d-flex justify-content-center align-items-baseline my-2">
              <h2 className="fw-bold mb-0" style={{ color: '#D4AF37' }}>
                {showBalance ? `₹${balance}` : '••••••'}
              </h2>
            </div>
            <div style={{
              display: 'inline-block',
              background: 'rgba(212, 175, 55, 0.1)',
              borderRadius: '20px',
              padding: '4px 12px'
            }}>
              <span style={{ color: '#D4AF37', fontSize: '12px' }}>
                <i className="fa fa-currency-dollar me-1"></i>
                ≈ ${showBalance ? (balance / usd).toFixed(2) : '••••'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions - Redesigned */}
      <div className="container mt-3">
        <div className="row g-3">
          <div className="col-4">
            <button 
              className="btn w-100 py-2"
              onClick={() => navigate('/deposite')}
              style={{
                background: 'linear-gradient(145deg, #D4AF37, #B8960C)',
                border: 'none',
                borderRadius: '16px',
                color: '#000',
                fontWeight: 'bold',
                fontSize: '13px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <i className="fa fa-download me-1"></i>
              Deposit
            </button>
          </div>
          <div className="col-4">
            <button 
              className="btn w-100 py-2"
              onClick={() => navigate('/tradebook')}
              style={{
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '16px',
                color: '#D4AF37',
                fontWeight: 'bold',
                fontSize: '13px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
              }}
            >
              <i className="fa fa-arrow-left-right me-1"></i>
              Trade
            </button>
          </div>
          <div className="col-4">
            <button 
              className="btn w-100 py-2"
              onClick={() => navigate('/withdrawl')}
              style={{
                background: 'rgba(255, 82, 82, 0.15)',
                border: '1px solid rgba(255, 82, 82, 0.3)',
                borderRadius: '16px',
                color: '#FF5252',
                fontWeight: 'bold',
                fontSize: '13px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 82, 82, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 82, 82, 0.15)';
              }}
            >
              <i className="fa fa-upload me-1"></i>
              Withdraw
            </button>
          </div>
        </div>
      </div>

      {/* Market Overview Section */}
      <div className="container mt-4 mb-4">
        <div style={{
          background: 'rgba(20, 20, 20, 0.8)',
          borderRadius: '24px',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          overflow: 'hidden'
        }}>
          <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="fw-bold mb-0" style={{ color: '#D4AF37', letterSpacing: '1px' }}>MARKET OVERVIEW</h6>
                <small style={{ color: '#888' }}>Real-time prices updated every 2s</small>
              </div>
              <div className="d-flex gap-1">
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: '#00C853',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s infinite'
                }}></div>
                <small style={{ color: '#00C853', fontSize: '10px' }}>LIVE</small>
              </div>
            </div>

            {/* Market Tabs - Redesigned */}
            <div className="d-flex gap-2 mb-4" style={{ overflowX: 'auto', paddingBottom: '4px' }}>
              {marketTabs.map((tab) => {
                const isActive = activeMarketTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    className="btn btn-sm"
                    onClick={() => setActiveMarketTab(tab.key)}
                    style={{
                      background: isActive ? `linear-gradient(145deg, ${tab.color}, ${tab.color}CC)` : 'rgba(26, 26, 26, 0.8)',
                      border: isActive ? 'none' : '1px solid rgba(212, 175, 55, 0.2)',
                      borderRadius: '30px',
                      padding: '6px 16px',
                      color: isActive ? '#000' : '#fff',
                      fontWeight: 'bold',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.borderColor = '#D4AF37';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                      }
                    }}
                  >
                    <i className={`bi ${tab.icon} me-1`}></i>
                    {tab.title}
                  </button>
                );
              })}
            </div>

            {/* Market Data */}
            <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '4px' }}>
              {renderMarketData()}
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
          
          .spin {
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          ::-webkit-scrollbar {
            width: 4px;
          }
          
          ::-webkit-scrollbar-track {
            background: #1a1a1a;
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: #D4AF37;
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: #B8960C;
          }
        `}
      </style>
    </div>
  );
};

export default WalletUI;